import Aparecer from './Aparecer';
import BarraProgresso from './BarraProgresso';
import type { InscricoesResumo } from '@/pages/gerenciar/painel/financeiro/relatorio/types';
import { formatarCentavos } from '@/pages/loja/utils';
import { percentual } from '@/pages/gerenciar/painel/financeiro/relatorio/utils';

function Contagem({ rotulo, valor, cor }: { rotulo: string; valor: number; cor: string }) {
  return (
    <div className="rounded-xl bg-background-50 px-3 py-3 text-center">
      <p className={`text-[clamp(1.3rem,7vw,1.9rem)] font-extrabold leading-none tabular-nums ${cor}`}>
        {valor}
      </p>
      <p className="mt-1 text-[12px] leading-tight text-foreground-500">{rotulo}</p>
    </div>
  );
}

export default function SecaoInscricoes({ ins }: { ins: InscricoesResumo }) {
  const pct = percentual(ins.pago_centavos, ins.devido_centavos);
  const publicos = (ins.por_publico ?? []).filter((p) => p.total > 0 || p.suplentes > 0);
  // Evento misto (equipe + campistas): uma linha por público e NADA de total geral.
  const misto = publicos.length > 1;
  const suplentesFora = misto
    ? publicos.reduce((s, p) => s + (Number(p.suplentes) || 0), 0)
    : Number(ins.suplentes ?? 0) || 0;

  return (
    <Aparecer>
      <section className="rel-secao rounded-2xl border border-background-200 bg-white p-4 shadow-[0_1px_3px_rgba(16,24,40,0.04)] md:p-5">
        <h2 className="text-[15px] font-bold text-foreground-950">Inscrições</h2>

        {misto ? (
          <>
            <div className="mt-3 space-y-3">
              {publicos.map((p) => (
                <div
                  key={`${p.publico}-${p.papel ?? 'x'}`}
                  className="rounded-xl border border-background-200 bg-background-50 px-3 py-3"
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                    <span className="text-[14px] font-semibold text-foreground-950">
                      {p.publico}
                    </span>
                    <span className="text-[13px] tabular-nums text-foreground-600">
                      {Number(p.total).toLocaleString('pt-BR')}{' '}
                      {p.total === 1 ? 'pessoa' : 'pessoas'} ·{' '}
                      {Number(p.quitadas).toLocaleString('pt-BR')}{' '}
                      {p.quitadas === 1 ? 'quitada' : 'quitadas'}
                    </span>
                  </div>
                  <p className="mt-1 text-[13px] tabular-nums text-foreground-700">
                    Pago{' '}
                    <strong className="font-semibold text-foreground-950">
                      {formatarCentavos(p.pago_centavos)}
                    </strong>
                    {' · '}
                    Em aberto{' '}
                    <strong className="font-semibold text-foreground-950">
                      {formatarCentavos(p.em_aberto_centavos)}
                    </strong>
                  </p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[13px] font-medium text-foreground-600">
              Suplentes: {Number(suplentesFora).toLocaleString('pt-BR')} (fora das contas)
            </p>
          </>
        ) : (
          <>
            <div className="mt-3">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <span className="text-[13px] text-foreground-500">Pago</span>
                <span className="text-[13px] font-semibold tabular-nums text-foreground-900">
                  {formatarCentavos(ins.pago_centavos)}{' '}
                  <span className="font-normal text-foreground-400">
                    de {formatarCentavos(ins.devido_centavos)}
                  </span>
                </span>
              </div>
              <div className="mt-2">
                <BarraProgresso pct={pct} />
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2 md:gap-3">
              <Contagem rotulo="Quitadas" valor={ins.quitadas} cor="text-success-600" />
              <Contagem rotulo="Pagando" valor={ins.parciais} cor="text-warning-600" />
              <Contagem rotulo="Sem pagamento" valor={ins.sem_pagamento} cor="text-danger-600" />
            </div>

            <p className="mt-3 text-[12px] leading-relaxed text-foreground-500">
              Pago pela ação entre amigos {formatarCentavos(ins.pago_pela_acao_centavos)} · Pago
              direto {formatarCentavos(ins.pago_direto_centavos)} · Em aberto{' '}
              {formatarCentavos(ins.em_aberto_centavos)}
            </p>
            {suplentesFora > 0 && (
              <p className="mt-1 text-[13px] font-medium text-foreground-600">
                Suplentes: {Number(suplentesFora).toLocaleString('pt-BR')} (fora das contas)
              </p>
            )}
          </>
        )}
      </section>
    </Aparecer>
  );
}